import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { PropertyService } from '../../services/property.service';
import { LesseeService } from '../../services/lessee.service';
import { NavigationContextService } from '../../services/navigation-context.service';

@Component({
  selector: 'app-invoice-pdf',
  templateUrl: './invoice-pdf.component.html',
  styleUrls: ['./invoice-pdf.component.scss']
})
export class InvoicePdfComponent implements OnInit, OnDestroy {
  loadError = false;
  invoiceId: string;
  objectUrl: string;
  private pdfBlob: Blob;
  /** PDF bytes, rendered inline by app-pdf-canvas-viewer and reused for share/download. */
  pdfBytes: ArrayBuffer;
  /** pdf.js could not draw the file; show the Open/Share/Download panel instead. */
  renderFailed = false;
  propertyAddressLine1: string;
  invoiceDueDate: string;
  isAdmin = false;
  deleting = false;
  sharing = false;
  canShare = false;
  private propertyId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private propertyService: PropertyService,
    private lesseeService: LesseeService,
    private navContext: NavigationContextService
  ) { }

  ngOnInit() {
    this.isAdmin = this.authService.isAdmin();
    this.canShare = typeof navigator !== 'undefined' && !!(navigator as any).share;
    this.invoiceId = this.route.snapshot.paramMap.get('id');
    let pdfPromise: Promise<Blob>;
    let invoicePromise: Promise<any>;
    let propertyPromise: Promise<any>;
    if (this.isAdmin) {
      this.propertyId = this.route.snapshot.queryParamMap.get('propertyId');
      pdfPromise = this.propertyService.getInvoicePdf({ id: this.propertyId }, this.invoiceId);
      invoicePromise = this.propertyService.getInvoices({ id: this.propertyId }).then((response: any) => {
        const invoices = (response && response.data && Array.isArray(response.data)) ? response.data : (Array.isArray(response) ? response : []);
        return invoices.find(i => i.id === this.invoiceId);
      });
      propertyPromise = this.propertyService.getProperties().then((props: any[]) => props.find(p => p.id === this.propertyId));
    } else {
      pdfPromise = this.lesseeService.getInvoicePdf(this.invoiceId);
      invoicePromise = this.lesseeService.getInvoices().then((response: any) => {
        const invoices = (response && response.data && Array.isArray(response.data)) ? response.data : (Array.isArray(response) ? response : []);
        return invoices.find(i => i.id === this.invoiceId);
      });
      propertyPromise = this.lesseeService.getLeasedProperty();
    }
    Promise.all([pdfPromise, invoicePromise, propertyPromise]).then((values) => {
      const rawBlob = values[0] as Blob;
      const invoice = values[1];
      const property = values[2];
      return this.readBlobAsArrayBuffer(rawBlob).then((buffer) => {
        const headerBytes = new Uint8Array(buffer, 0, Math.min(5, buffer.byteLength));
        let header = '';
        for (let i = 0; i < headerBytes.length; i++) {
          header += String.fromCharCode(headerBytes[i]);
        }
        if (buffer.byteLength < 5 || header.indexOf('%PDF') !== 0) {
          console.error('Invoice PDF response was not a PDF. size=', buffer.byteLength, 'header=', header);
          this.loadError = true;
          return;
        }

        // Keep a copy of the bytes for share/download File construction.
        this.pdfBytes = buffer.slice(0);
        this.pdfBlob = new Blob([this.pdfBytes], { type: 'application/pdf' });
        this.objectUrl = URL.createObjectURL(this.pdfBlob);
        if (invoice) {
          this.invoiceDueDate = invoice.dueDate;
        }
        if (property) {
          this.propertyAddressLine1 = property.addressLine1;
          if (!this.propertyId && property.id) {
            this.propertyId = property.id;
          }
        }
        // Stay on this screen so Share can attach a named File + canned message.
        // Auto-navigating to the blob: URL made iOS Messages show "blob:" / Unknown.pdf.
      });
    }).catch((err) => {
      console.error('Error loading invoice PDF:', err);
      this.loadError = true;
    });
  }

  private readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error || new Error('Failed to read PDF blob'));
      reader.readAsArrayBuffer(blob);
    });
  }

  private sanitizeFilePart(value: string, fallback: string): string {
    const cleaned = (value || '')
      .replace(/[^\w\s.-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_-]+|[_-]+$/g, '');
    return cleaned || fallback;
  }

  private buildFileName(): string {
    const addr = this.sanitizeFilePart(this.propertyAddressLine1, 'invoice');
    const due = this.sanitizeFilePart(this.invoiceDueDate || this.invoiceId, 'invoice');
    return addr + '_' + due + '.pdf';
  }

  private buildShareMessage(): string {
    const due = this.invoiceDueDate || 'your upcoming due date';
    return 'Hey here is your invoice for ' + due + ' Thank you';
  }

  private isAppleTouchDevice(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua)
      || ((navigator as any).platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  }

  onRenderFailed() {
    this.renderFailed = true;
  }

  /** Open in the device PDF viewer (manual — keeps Share working from this screen). */
  openInViewer() {
    if (!this.objectUrl) {
      return;
    }
    window.location.href = this.objectUrl;
  }

  download() {
    if (!this.pdfBytes) {
      return;
    }
    const fileName = this.buildFileName();
    const blob = new Blob([this.pdfBytes], { type: 'application/pdf' });
    this.saveBlobReliably(blob, fileName);
  }

  /** Prefer Web Share API (named File) for reliable iOS save-to-Files / share sheet.
   * Falls back to traditional download for environments where share is unavailable. */
  private saveBlobReliably(blob: Blob, fileName: string) {
    const nav: any = (typeof navigator !== 'undefined') ? navigator : null;
    const tryWebShareFile = async (): Promise<boolean> => {
      if (!nav || typeof nav.share !== 'function') {
        return false;
      }
      try {
        const file = new File([blob], fileName, { type: 'application/pdf', lastModified: Date.now() });
        const canShareFiles = !nav.canShare || nav.canShare({ files: [file] });
        if (canShareFiles) {
          await nav.share({ files: [file] });
          return true;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return true;
        }
      }
      return false;
    };
    tryWebShareFile().then((did) => {
      if (did) {
        return;
      }
      // Fallback
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  deletePdf() {
    if (!this.isAdmin || !this.propertyId || this.deleting) {
      return;
    }
    if (!confirm('Are you sure you want to delete this PDF? You can regenerate it later.')) {
      return;
    }
    this.deleting = true;
    this.propertyService.deleteInvoicePdf({ id: this.propertyId }, this.invoiceId).then(() => {
      this.deleting = false;
      this.back();
    }).catch((error) => {
      this.deleting = false;
      console.error('Error deleting PDF:', error);
      alert('Failed to delete PDF. Please try again.');
    });
  }

  ngOnDestroy() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  share() {
    if (!this.pdfBytes || this.sharing) {
      return;
    }
    const n: any = navigator;
    if (!n || typeof n.share !== 'function') {
      this.download();
      return;
    }

    const message = this.buildShareMessage();
    const fileName = this.buildFileName();
    // Fresh bytes + explicit name — iOS labels blob-URL shares as Unknown.pdf.
    const file = new File([this.pdfBytes.slice(0)], fileName, {
      type: 'application/pdf',
      lastModified: Date.now()
    });

    this.sharing = true;

    const finish = () => { this.sharing = false; };

    const shareFilesAndText = () => {
      // Never include a blob: url — iMessage shows it as the text "blob:".
      const data: any = {
        files: [file],
        text: message,
        title: message
      };
      return n.share(data);
    };

    const maybeCopyMessage = () => {
      // iMessage often drops Web Share `text` when a file is attached.
      // Copy the canned message so it can be pasted into the compose field.
      if (!this.isAppleTouchDevice() || !n.clipboard || typeof n.clipboard.writeText !== 'function') {
        return Promise.resolve();
      }
      return n.clipboard.writeText(message).catch(() => {});
    };

    const canShareFiles = !n.canShare || n.canShare({ files: [file] });

    maybeCopyMessage().then(() => {
      if (canShareFiles) {
        return shareFilesAndText();
      }
      // Files not supported — share the canned message only (no blob URL).
      return n.share({ text: message, title: message });
    }).then(finish).catch((err) => {
      finish();
      if (err && err.name === 'AbortError') {
        return;
      }
      console.error('Error sharing invoice PDF:', err);
      // Last resort: download with the correct filename.
      this.download();
    });
  }

  back() {
    if (this.authService.isAdmin()) {
      const propertyId = this.route.snapshot.queryParamMap.get('propertyId')
        || this.navContext.propertyId;
      if (propertyId) {
        this.router.navigate(['/admin-invoices'], { queryParams: { propertyId } });
        return;
      }
      this.router.navigate(['/admin-invoices']);
      return;
    }
    this.router.navigate(['/lessee-landing']);
  }
}
